/**
 * OrderMissionWorkspaces — Overview / Operations / Tracking / Customer / Finance / Activity
 * Sprint 8.6 Mission Control detail body (left column).
 */

import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Tooltip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
} from "@mui/material";
import {
  Person,
  Phone,
  WhatsApp,
  ContentCopy,
  Schedule,
  ReceiptLong,
  Timeline as TimelineIcon,
  Build,
  LocalShipping,
  AccountBalance,
  AddLink,
  Link as LinkIcon,
  FileDownload,
} from "@mui/icons-material";
import { format } from "date-fns";
import { ConfigProvider } from "antd";
import { bubakiiTheme } from "../../../theme/bubakiiTheme.js";
import DeliveryProgressBar from "./DeliveryProgressBar.jsx";
import { StatusCommunicationTracker } from "./StatusCommunicationTracker.jsx";
import OrderJourneyTimeline from "./OrderJourneyTimeline.jsx";
import OrderJourneyBar from "./OrderJourneyBar.jsx";
import OrderParticipants from "./OrderParticipants.jsx";
import OrderTripAssignmentCard from "./OrderTripAssignmentCard.jsx";
import CustomerNotesPanel from "../../customers/components/CustomerNotesPanel.jsx";
import ConversationSummaryPanel from "../../leads/components/ConversationSummaryPanel.jsx";
import RouteMapWrapper from "../../../components/RouteMap/RouteMapWrapper.jsx";
import { formatPickupTime } from "../../../shared/utils/formatPickupTime.js";
import { getOrderStatusExplanation } from "../../../shared/utils/statusExplanations";
import { getRouteCoordinates } from "../services/orderMissionSummary.js";
import { openAppPopup } from "@/shared/utils/openAppPopup.js";
import { useOrderTrackingLink } from "../hooks/useOrderTrackingLink.js";
import { copyToClipboard } from "../../leads/services/communicationTemplateService.js";

const MONEY_ROUNDING_TOLERANCE = 0.01;

const formatMoney = (value) => {
  const num = Number(value || 0);
  return `R${num.toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/** Calendar date in Africa/Johannesburg (avoids UTC-midnight off-by-one). */
const formatMoveDateSa = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-ZA", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Africa/Johannesburg",
  });
};

const getInvoiceId = (inv) => inv?.id || inv?._id;

const computeInvoiceRollup = (invoices = []) => {
  const list = Array.isArray(invoices) ? invoices : [];
  let total = 0;
  let totalPaid = 0;
  let totalRefunded = 0;
  let balance = 0;

  for (const inv of list) {
    if (inv?.status === "cancelled") continue;
    const invTotal = Number(inv?.total || 0);
    total += invTotal;
    const summary = inv?.paymentSummary;
    if (summary && typeof summary === "object") {
      totalPaid += Number(summary.totalPaid || 0);
      totalRefunded += Number(summary.totalRefunded || 0);
      balance += Number(summary.balance || 0);
      continue;
    }
    const paidFromPayments = (inv?.payments || []).reduce(
      (sum, p) => sum + Number(p?.amount || 0),
      0
    );
    const refundedForBalance = (inv?.refunds || [])
      .filter((r) => r?.status === "completed")
      .filter(
        (r) => (r?.classification || "payment_reversal") === "payment_reversal"
      )
      .reduce((sum, r) => sum + Number(r?.amount || 0), 0);
    const netPaid = Math.max(paidFromPayments - refundedForBalance, 0);
    totalPaid += netPaid;
    totalRefunded += refundedForBalance;
    balance += Math.max(invTotal - netPaid, 0);
  }

  return {
    count: list.length,
    total,
    totalPaid,
    totalRefunded,
    balance,
    isClear: balance <= MONEY_ROUNDING_TOLERANCE,
  };
};

function RouteMapCard({ order }) {
  const routeCoords = getRouteCoordinates(order);
  return (
    <Card sx={{ height: "100%", minHeight: 240 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Route map
        </Typography>
        {!routeCoords.hasRoute ? (
          <Alert severity="info" data-testid="map-empty-state">
            Map unavailable — pickup/delivery coordinates are missing.
          </Alert>
        ) : (
          <Box sx={{ height: 220, borderRadius: 1, overflow: "hidden" }}>
            <RouteMapWrapper
              pickup={order.pickup?.address}
              delivery={order.delivery?.address}
              pickupAddress={{
                address: order.pickup?.address,
                coordinates: routeCoords.pickup,
              }}
              deliveryAddress={{
                address: order.delivery?.address,
                coordinates: routeCoords.delivery,
              }}
              autoFitBounds
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function LoadIntelligenceCard({ order, orderId, navigate }) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          flexWrap="wrap"
        >
          <Typography variant="h6">Load Intelligence</Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`/orders/${orderId}/edit?focus=items`)}
            sx={{ textTransform: "none" }}
          >
            Add load details
          </Button>
        </Box>
        <Divider sx={{ my: 2 }} />
        {Array.isArray(order?.items) && order.items.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800, width: 60 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Item</TableCell>
                  <TableCell sx={{ fontWeight: 800, width: 90 }} align="right">
                    Qty
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, width: 110 }}>Weight</TableCell>
                  <TableCell sx={{ fontWeight: 800, width: 140 }}>Handling</TableCell>
                  <TableCell sx={{ fontWeight: 800, width: 140 }}>Drop</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {order.items.map((item, idx) => {
                  const desc =
                    item?.description || item?.content || `Item ${idx + 1}`;
                  const qty = Number(item?.quantity || 1);
                  const weight = item?.weight ? `${item.weight} kg` : "—";
                  const drop = item?.dropZone || order?.dropZone || "—";
                  const text = String(desc).toLowerCase();
                  const isFragile =
                    Boolean(item?.fragile) ||
                    text.includes("fragile") ||
                    text.includes("glass") ||
                    text.includes("mirror");
                  const needsDetails =
                    !item?.weight &&
                    !item?.volume &&
                    !item?.handling &&
                    !item?.fragile;
                  return (
                    <TableRow key={idx} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell
                        sx={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {desc}
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                          {item?.type && item.type !== "text" ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={String(item.type).toUpperCase()}
                            />
                          ) : null}
                          {needsDetails ? (
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label="Missing load details"
                            />
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {Number.isFinite(qty) ? qty : 1}
                      </TableCell>
                      <TableCell>{weight}</TableCell>
                      <TableCell>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {isFragile ? (
                            <Chip size="small" color="warning" label="Fragile" />
                          ) : (
                            <Chip size="small" variant="outlined" label="Standard" />
                          )}
                          {item?.handling ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={String(item.handling).toUpperCase()}
                            />
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell>{drop}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="warning">
            No items captured yet. This is risky for ops—add quantities/handling notes
            before dispatch.
          </Alert>
        )}
        {order?.totalWeight ? (
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: "primary.light",
              borderRadius: 1,
            }}
          >
            <Typography variant="body1" fontWeight="bold">
              Total Weight: {order.totalWeight} kg
            </Typography>
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * @param {Object} props
 */
export default function OrderMissionWorkspaces({
  order,
  orderId,
  trip,
  quote,
  lead,
  invoices,
  loadingLinked,
  hasBooking,
  activeTab,
  setActiveTab,
  navigate,
  canLinkInvoice,
  canManageInvoices,
  canViewInvoices,
  customerEmail,
  customerWhatsAppLink,
  getStatusColor,
  handleCopyCustomerDetails,
  handleViewBooking,
  handleViewLedger,
  handleCreateInvoice,
  onAddCharges,
  onDownloadBillingStatement,
  setLinkInvoiceModalOpen,
  handleTripAssignmentChange,
  handleDownloadDriverPdf,
  handleDownloadClientPdf,
}) {
  const orderRef = order?.id || order?.jobId;
  const {
    data: trackingLink,
    isLoading: trackingLinkLoading,
    refetch: refetchTrackingLink,
  } = useOrderTrackingLink(orderRef, { enabled: Boolean(orderRef) });
  const trackerUrl = trackingLink?.trackingUrl || null;
  const [linkCopied, setLinkCopied] = useState(false);

  const handleCopyTrackingLink = async () => {
    if (!trackerUrl) return;
    const ok = await copyToClipboard(trackerUrl);
    if (ok) {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  return (
    <>
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        <Tab label="Overview" />
        <Tab icon={<Build />} iconPosition="start" label="Operations" />
        <Tab icon={<LocalShipping />} iconPosition="start" label="Tracking" />
        <Tab icon={<Person />} iconPosition="start" label="Customer" />
        <Tab icon={<ReceiptLong />} iconPosition="start" label="Finance" />
        <Tab icon={<TimelineIcon />} iconPosition="start" label="Activity" />
      </Tabs>

      {activeTab === 0 && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={5}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Person />
                    <Typography variant="h6">Customer</Typography>
                  </Box>
                  <Typography variant="body1" fontWeight={700}>
                    {order.customer || "N/A"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {order.customerPhone || "No phone"}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1.5 }}
                  >
                    {customerEmail || "No email"}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {order.customerPhone ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Phone />}
                        href={`tel:${order.customerPhone}`}
                        sx={{ textTransform: "none" }}
                      >
                        Call
                      </Button>
                    ) : null}
                    {customerWhatsAppLink ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<WhatsApp />}
                        href={customerWhatsAppLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textTransform: "none" }}
                      >
                        WhatsApp
                      </Button>
                    ) : null}
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setActiveTab(3)}
                      sx={{ textTransform: "none" }}
                    >
                      Full profile
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={7}>
              <RouteMapCard order={order} />
            </Grid>
          </Grid>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Overview
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Order ID
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {order.id}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <Tooltip title={getOrderStatusExplanation(order.status)}>
                      <Chip
                        label={order.status?.replaceAll("_", " ").toUpperCase()}
                        color={getStatusColor(order.status)}
                        size="small"
                      />
                    </Tooltip>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Move date
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {order.booking?.moveDate
                      ? formatMoveDateSa(order.booking.moveDate)
                      : "Not scheduled"}
                  </Typography>
                </Grid>
                {formatPickupTime(order.booking) ? (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Pickup Time
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatPickupTime(order.booking)}
                    </Typography>
                  </Grid>
                ) : null}
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Addresses
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Pickup
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {order.pickup?.address || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Delivery
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {order.delivery?.address || "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {order.deliveryDrops && order.deliveryDrops.length > 0 ? (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <ConfigProvider theme={bubakiiTheme}>
                  <DeliveryProgressBar order={order} />
                </ConfigProvider>
              </CardContent>
            </Card>
          ) : null}

          <LoadIntelligenceCard
            order={order}
            orderId={orderId}
            navigate={navigate}
          />

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Notes & documents
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setActiveTab(5)}
                  sx={{ textTransform: "none" }}
                >
                  Activity & notes
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleDownloadDriverPdf}
                  sx={{ textTransform: "none" }}
                >
                  Driver PDF
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleDownloadClientPdf}
                  sx={{ textTransform: "none" }}
                >
                  Client PDF
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === 1 && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <OrderParticipants orderId={orderId} />
            </CardContent>
          </Card>
          <OrderTripAssignmentCard
            order={order}
            onAssignmentChange={handleTripAssignmentChange}
          />
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={2}
              >
                <Typography variant="h6">Booking & Scheduling</Typography>
                {hasBooking ? (
                  <Button
                    variant="outlined"
                    startIcon={<Schedule />}
                    onClick={handleViewBooking}
                    sx={{ textTransform: "none" }}
                  >
                    Open Booking
                  </Button>
                ) : null}
              </Box>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Booking Status
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {order.booking?.status
                      ? String(order.booking.status).replaceAll("_", " ").toUpperCase()
                      : "Not scheduled"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Move Date
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {order.booking?.moveDate
                      ? formatMoveDateSa(order.booking.moveDate)
                      : "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === 2 && (
        <>
          <OrderJourneyBar order={order} trip={trip} />
          <Box sx={{ mb: 3 }}>
            <RouteMapCard order={order} />
          </Box>
          {order.deliveryDrops && order.deliveryDrops.length > 0 ? (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <ConfigProvider theme={bubakiiTheme}>
                  <DeliveryProgressBar order={order} />
                </ConfigProvider>
              </CardContent>
            </Card>
          ) : null}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Customer tracker
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Share this link if the customer did not receive their tracking
                message.
              </Typography>
              <Divider sx={{ my: 2 }} />
              {trackingLinkLoading ? (
                <CircularProgress size={24} />
              ) : trackerUrl ? (
                <Stack spacing={2}>
                  <Typography
                    variant="body2"
                    sx={{
                      wordBreak: "break-all",
                      fontFamily: "monospace",
                      bgcolor: "grey.50",
                      p: 1.5,
                      borderRadius: 1,
                    }}
                  >
                    {trackerUrl}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      variant="outlined"
                      startIcon={<LinkIcon />}
                      href={trackerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textTransform: "none" }}
                    >
                      Open public tracker
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<ContentCopy />}
                      onClick={handleCopyTrackingLink}
                      sx={{ textTransform: "none" }}
                    >
                      {linkCopied ? "Copied!" : "Copy link"}
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => refetchTrackingLink()}
                      sx={{ textTransform: "none" }}
                    >
                      Refresh link
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Tracker link unavailable.
                </Typography>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === 3 && (
        <Card>
          <CardContent>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              flexWrap="wrap"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Person />
                <Typography variant="h6">Customer Snapshot</Typography>
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap">
                {order.customerPhone ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Phone />}
                    href={`tel:${order.customerPhone}`}
                    sx={{ textTransform: "none" }}
                  >
                    Call
                  </Button>
                ) : null}
                {customerWhatsAppLink ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<WhatsApp />}
                    href={customerWhatsAppLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textTransform: "none" }}
                  >
                    WhatsApp
                  </Button>
                ) : null}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ContentCopy />}
                  onClick={handleCopyCustomerDetails}
                  sx={{ textTransform: "none" }}
                >
                  Copy Details
                </Button>
                {order.customerId ? (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() =>
                      openAppPopup(`/customers/${order.customerId}`, {
                        title: `Customer ${order.customerId}`,
                      })
                    }
                    sx={{ textTransform: "none" }}
                  >
                    Open Customer
                  </Button>
                ) : null}
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Customer
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {order.customer || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Phone
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {order.customerPhone || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {customerEmail || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Pickup time
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatPickupTime(order.booking) || "Not scheduled"}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {activeTab === 4 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={2}
            >
              <Typography variant="h6">Finance</Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant="outlined"
                  startIcon={<AccountBalance />}
                  onClick={handleViewLedger}
                  sx={{ textTransform: "none" }}
                >
                  View Ledger
                </Button>
                {canLinkInvoice && order && (order.jobId || order.id) ? (
                  <Button
                    variant="outlined"
                    startIcon={<AddLink />}
                    onClick={() => setLinkInvoiceModalOpen(true)}
                    sx={{ textTransform: "none" }}
                  >
                    Link existing invoice
                  </Button>
                ) : null}
                {canViewInvoices ? (
                  <Button
                    variant="outlined"
                    startIcon={<FileDownload />}
                    onClick={onDownloadBillingStatement}
                    disabled={invoices.length === 0}
                    sx={{ textTransform: "none" }}
                  >
                    Billing PDF
                  </Button>
                ) : null}
                {canManageInvoices ? (
                  <Button
                    variant="contained"
                    startIcon={<ReceiptLong />}
                    onClick={onAddCharges || handleCreateInvoice}
                    sx={{ textTransform: "none" }}
                  >
                    Add Charges
                  </Button>
                ) : null}
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            {loadingLinked ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {(() => {
                    const rollup = computeInvoiceRollup(invoices);
                    return (
                      <>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2" color="text.secondary">
                            Total invoiced
                          </Typography>
                          <Typography variant="h6">
                            {formatMoney(rollup.total)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2" color="text.secondary">
                            Total paid (net)
                          </Typography>
                          <Typography variant="h6">
                            {formatMoney(rollup.totalPaid)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2" color="text.secondary">
                            Total refunded
                          </Typography>
                          <Typography
                            variant="h6"
                            color={
                              rollup.totalRefunded > 0
                                ? "error.main"
                                : "text.primary"
                            }
                          >
                            {formatMoney(rollup.totalRefunded)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2" color="text.secondary">
                            Balance
                          </Typography>
                          <Typography
                            variant="h6"
                            color={
                              rollup.isClear ? "success.main" : "warning.main"
                            }
                          >
                            {formatMoney(rollup.balance)}
                          </Typography>
                        </Grid>
                      </>
                    );
                  })()}
                </Grid>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Paid</TableCell>
                        <TableCell align="right">Balance</TableCell>
                        <TableCell>Due</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7}>
                            <Typography variant="body2" color="text.secondary">
                              No invoices linked to this job yet.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        invoices.map((inv) => {
                          const invId = getInvoiceId(inv);
                          const summary =
                            inv.paymentSummary || computeInvoiceRollup([inv]);
                          const paid =
                            inv.paymentSummary?.totalPaid ??
                            summary.totalPaid ??
                            0;
                          const bal =
                            inv.status === "cancelled"
                              ? 0
                              : (inv.paymentSummary?.balance ??
                                summary.balance ??
                                0);
                          return (
                            <TableRow key={invId}>
                              <TableCell>{invId}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={String(inv.status || "draft")
                                    .replaceAll("_", " ")
                                    .toUpperCase()}
                                />
                              </TableCell>
                              <TableCell align="right">
                                {formatMoney(inv.total || 0)}
                              </TableCell>
                              <TableCell align="right">
                                {formatMoney(paid)}
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  color={
                                    Number(bal) > 0.01
                                      ? "warning.main"
                                      : "success.main"
                                  }
                                >
                                  {formatMoney(bal)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {inv.dueDate
                                  ? format(new Date(inv.dueDate), "PP")
                                  : "N/A"}
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  sx={{ textTransform: "none" }}
                                  onClick={() =>
                                    openAppPopup(`/invoices/${invId}`, {
                                      title: `Invoice ${invId}`,
                                    })
                                  }
                                >
                                  View
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 5 && (
        <>
          <OrderJourneyTimeline
            order={order}
            quote={quote}
            lead={lead}
            invoices={invoices}
          />
          <Card sx={{ mb: 3, mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Communications
              </Typography>
              <Divider sx={{ my: 2 }} />
              <ConfigProvider theme={bubakiiTheme}>
                <StatusCommunicationTracker
                  orderId={order.id}
                  currentStatus={order.status}
                />
              </ConfigProvider>
            </CardContent>
          </Card>
          {order.customerId ? (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <CustomerNotesPanel customerId={order.customerId} />
              </CardContent>
            </Card>
          ) : null}
          {lead?.id || lead?._id ? (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <ConversationSummaryPanel leadId={lead.id || lead._id} />
              </CardContent>
            </Card>
          ) : null}
        </>
      )}
    </>
  );
}
